/**
 * Ask: a small structured query language over live state (DesignSystem 5.1). It reads like
 * English and needs no model: "sessions failed today", "machines offline", "spend by project
 * this week", "top 5 sessions by tokens on build-01". Everything runs in the browser over
 * the store; nothing is sent anywhere.
 *
 * The same clauses are the filter language of every list: a page's `where=` parameter holds
 * them (without the entity), so an answer becomes a saved view by copying its clauses.
 *
 * Grammar, loosely (order is free, unknown words become a text search):
 *
 *   query   := [count|how many] [top N] entity clause*
 *   entity  := sessions | machines | projects | audit | spend | pipelines | worktrees
 *   clause  := status-word                      failed, waiting, offline, outdated, ...
 *            | time                             today, yesterday, this week, last 3 hours, since monday
 *            | started time                     sessions started today (default: active in the window)
 *            | on <machine> | in <project>      names match as typed or by unique prefix
 *            | by|per <group field>            by project, by machine, by status, by model, by day
 *            | sort by <field> [asc|desc]      also: most tokens, newest, oldest, top N by cost
 *            | <field> > N                      tokens > 1m, load over 80%, turns at least 20
 *            | model <name> | opus | sonnet    model family
 *            | with subagents|workflows|changes, started here, actor <name>, action <kind>
 *            | "quoted text"
 */
import type { ActionKind } from '@orgus/protocol';
import { ACTION_KINDS } from '@orgus/protocol';

export type AskEntity = 'sessions' | 'machines' | 'projects' | 'audit' | 'spend' | 'pipelines' | 'checkouts';
export const ASK_ENTITIES: readonly AskEntity[] = ['sessions', 'machines', 'projects', 'audit', 'spend', 'pipelines', 'checkouts'];

export type TimeWindow =
  | { kind: 'today' }
  | { kind: 'yesterday' }
  | { kind: 'week' }
  | { kind: 'lastweek' }
  | { kind: 'month' }
  /** The last `ms` milliseconds; `n` and `unit` keep the words for display. */
  | { kind: 'last'; n: number; unit: 'minute' | 'hour' | 'day' | 'week' };

export type CompareField = 'tokens' | 'turns' | 'load' | 'memory' | 'cost' | 'subagents' | 'behind' | 'changes';
export type CompareOp = '>' | '<' | '>=' | '<=' | '=';

export type Clause =
  | { kind: 'status'; value: string }
  | { kind: 'time'; field: 'activity' | 'started'; window: TimeWindow }
  | { kind: 'machine'; name: string }
  | { kind: 'project'; name: string }
  | { kind: 'model'; value: string }
  | { kind: 'text'; value: string }
  | { kind: 'compare'; field: CompareField; op: CompareOp; value: number }
  /** `exact` (written `actor:login`) matches one person; otherwise any actor containing the words. */
  | { kind: 'actor'; value: string; exact?: boolean }
  | { kind: 'action'; value: ActionKind }
  | { kind: 'has'; value: 'subagents' | 'workflows' | 'changes' }
  | { kind: 'origin'; value: 'orgus' };

export type GroupField = 'project' | 'machine' | 'status' | 'model' | 'day' | 'actor' | 'action' | 'os' | 'result';
export type SortField = 'activity' | 'started' | 'tokens' | 'cost' | 'turns' | 'name' | 'load' | 'memory' | 'time';

export interface AskQuery {
  entity: AskEntity;
  /** True when the text named the entity (false: it came from the page or a default). */
  entityNamed: boolean;
  clauses: Clause[];
  groupBy: GroupField | null;
  sort: { field: SortField; dir: 'asc' | 'desc' } | null;
  limit: number | null;
  count: boolean;
  raw: string;
}

/** Names the parser resolves "on build" and "in qawl" against. */
export interface AskContext {
  machines: readonly string[];
  projects: readonly string[];
  /** Entity when the text names none (the page you are on). */
  defaultEntity?: AskEntity;
  /**
   * The entity is given and words never change it: a page's `where=` holds only clauses, so
   * "cost > 1" on Sessions is a comparison, not a question about spend.
   */
  fixedEntity?: boolean;
}

const ENTITY_WORDS: Record<string, AskEntity> = {
  session: 'sessions',
  sessions: 'sessions',
  conversation: 'sessions',
  conversations: 'sessions',
  transcript: 'sessions',
  transcripts: 'sessions',
  machine: 'machines',
  machines: 'machines',
  node: 'machines',
  nodes: 'machines',
  host: 'machines',
  hosts: 'machines',
  computer: 'machines',
  computers: 'machines',
  project: 'projects',
  projects: 'projects',
  repo: 'projects',
  repos: 'projects',
  repository: 'projects',
  repositories: 'projects',
  audit: 'audit',
  action: 'audit',
  actions: 'audit',
  log: 'audit',
  history: 'audit',
  spend: 'spend',
  spending: 'spend',
  cost: 'spend',
  costs: 'spend',
  money: 'spend',
  usage: 'spend',
  pipeline: 'pipelines',
  pipelines: 'pipelines',
  queue: 'pipelines',
  queues: 'pipelines',
  worktree: 'checkouts',
  worktrees: 'checkouts',
  checkout: 'checkouts',
  checkouts: 'checkouts',
  branch: 'checkouts',
  branches: 'checkouts',
};

/** Status words per entity: alias to canonical value. */
export const STATUS_WORDS: Record<AskEntity, Record<string, string>> = {
  sessions: {
    working: 'working',
    busy: 'working',
    waiting: 'waiting',
    blocked: 'waiting',
    asking: 'waiting',
    stuck: 'waiting',
    idle: 'idle',
    ended: 'ended',
    done: 'ended',
    finished: 'ended',
    closed: 'ended',
    stopped: 'ended',
    live: 'live',
    active: 'live',
    running: 'live',
    open: 'live',
    failed: 'failed',
    failing: 'failed',
    errored: 'failed',
    broken: 'failed',
  },
  machines: {
    online: 'online',
    up: 'online',
    connected: 'online',
    offline: 'offline',
    down: 'offline',
    disconnected: 'offline',
    dead: 'offline',
    stale: 'stale',
    silent: 'stale',
    outdated: 'outdated',
    old: 'outdated',
    busy: 'busy',
    loaded: 'busy',
    idle: 'idle',
    quiet: 'idle',
  },
  projects: { active: 'active', live: 'active', busy: 'active', idle: 'idle', quiet: 'idle', dirty: 'dirty' },
  audit: {
    failed: 'error',
    failures: 'error',
    failing: 'error',
    errors: 'error',
    error: 'error',
    refused: 'error',
    denied: 'error',
    succeeded: 'ok',
    successful: 'ok',
    success: 'ok',
    ok: 'ok',
    passed: 'ok',
  },
  spend: {},
  pipelines: { running: 'running', paused: 'paused', idle: 'idle', down: 'down', failed: 'error', error: 'error', broken: 'error' },
  checkouts: { dirty: 'dirty', clean: 'clean', behind: 'behind', ahead: 'ahead', stale: 'stale' },
};

/** Status words that name an entity on their own ("offline" alone means machines). */
const STATUS_IMPLIES: Record<string, AskEntity> = {
  offline: 'machines',
  online: 'machines',
  disconnected: 'machines',
  outdated: 'machines',
  waiting: 'sessions',
  working: 'sessions',
  paused: 'pipelines',
  dirty: 'checkouts',
};

const GROUP_WORDS: Record<string, GroupField> = {
  project: 'project',
  projects: 'project',
  repo: 'project',
  machine: 'machine',
  machines: 'machine',
  node: 'machine',
  host: 'machine',
  status: 'status',
  state: 'status',
  model: 'model',
  models: 'model',
  day: 'day',
  days: 'day',
  date: 'day',
  daily: 'day',
  actor: 'actor',
  person: 'actor',
  user: 'actor',
  who: 'actor',
  action: 'action',
  kind: 'action',
  type: 'action',
  os: 'os',
  platform: 'os',
  result: 'result',
};

const SORT_WORDS: Record<string, SortField> = {
  tokens: 'tokens',
  token: 'tokens',
  usage: 'tokens',
  cost: 'cost',
  spend: 'cost',
  money: 'cost',
  turns: 'turns',
  turn: 'turns',
  activity: 'activity',
  recent: 'activity',
  latest: 'activity',
  updated: 'activity',
  started: 'started',
  age: 'started',
  start: 'started',
  name: 'name',
  title: 'name',
  load: 'load',
  cpu: 'load',
  memory: 'memory',
  mem: 'memory',
  ram: 'memory',
  time: 'time',
  when: 'time',
};

const COMPARE_WORDS: Record<string, CompareField> = {
  tokens: 'tokens',
  token: 'tokens',
  turns: 'turns',
  turn: 'turns',
  load: 'load',
  cpu: 'load',
  memory: 'memory',
  mem: 'memory',
  ram: 'memory',
  cost: 'cost',
  spend: 'cost',
  subagents: 'subagents',
  subagent: 'subagents',
  behind: 'behind',
  changes: 'changes',
  changed: 'changes',
};

const OPS: Record<string, CompareOp> = {
  '>': '>',
  '<': '<',
  '>=': '>=',
  '<=': '<=',
  '=': '=',
  over: '>',
  above: '>',
  exceeding: '>',
  under: '<',
  below: '<',
};
const OP_PHRASES: [string[], CompareOp][] = [
  [['more', 'than'], '>'],
  [['greater', 'than'], '>'],
  [['less', 'than'], '<'],
  [['fewer', 'than'], '<'],
  [['at', 'least'], '>='],
  [['at', 'most'], '<='],
];

const MODELS = ['opus', 'sonnet', 'haiku'];

const FILLER = new Set([
  'show',
  'me',
  'list',
  'all',
  'the',
  'which',
  'what',
  'are',
  'is',
  'were',
  'was',
  'that',
  'have',
  'has',
  'had',
  'do',
  'did',
  'please',
  'find',
  'get',
  'any',
  'my',
  'a',
  'an',
  'there',
  'currently',
  'right',
  'now',
  'every',
  'each',
  'from',
  'during',
  'for',
  'of',
  'and',
  'who',
  'whose',
  'where',
  'went',
  'gone',
  'got',
  'been',
  'being',
  'in',
  'on',
  'to',
]);

const UNITS: Record<string, 'minute' | 'hour' | 'day' | 'week'> = {
  m: 'minute',
  min: 'minute',
  mins: 'minute',
  minute: 'minute',
  minutes: 'minute',
  h: 'hour',
  hr: 'hour',
  hrs: 'hour',
  hour: 'hour',
  hours: 'hour',
  d: 'day',
  day: 'day',
  days: 'day',
  w: 'week',
  week: 'week',
  weeks: 'week',
};

const ACTION_WORDS: Record<string, ActionKind> = {
  stop: 'session.stop',
  stops: 'session.stop',
  launch: 'session.start',
  launches: 'session.start',
  pause: 'pipeline.pause',
  pauses: 'pipeline.pause',
  resume: 'pipeline.resume',
  resumes: 'pipeline.resume',
  restart: 'pipeline.restart',
  restarts: 'pipeline.restart',
  removal: 'worktree.remove',
  removals: 'worktree.remove',
  command: 'job.run',
  commands: 'job.run',
};

/** Parse "1.5m", "20k", "80%", "0.8", "$5" into a number. Percent becomes a fraction. */
export function parseNumber(s: string): number | null {
  const m = /^\$?(\d+(?:\.\d+)?)(k|m|b|%)?$/i.exec(s.replace(/,/g, ''));
  if (!m) return null;
  const n = Number(m[1]);
  const suffix = m[2]?.toLowerCase();
  if (suffix === 'k') return n * 1e3;
  if (suffix === 'm') return n * 1e6;
  if (suffix === 'b') return n * 1e9;
  if (suffix === '%') return n / 100;
  return n;
}

/** Split into words, keeping "quoted text" and operators together. */
export function tokenize(text: string): string[] {
  const out: string[] = [];
  const re = /([a-z]+):"([^"]*)"|"([^"]*)"|'([^']*)'|(>=|<=|[<>=])|([^\s"'<>=]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m[1] != null) out.push(`${m[1]}:${m[2]}`);
    else if (m[3] != null || m[4] != null) out.push(`"${m[3] ?? m[4]}"`);
    else if (m[5]) out.push(m[5]);
    else if (m[6]) out.push(m[6]);
  }
  return out;
}

function unquote(word: string): string {
  return word.length > 1 && word.startsWith('"') && word.endsWith('"') ? word.slice(1, -1) : word;
}

function resolveName(word: string, names: readonly string[]): string | null {
  const w = unquote(word).toLowerCase();
  const exact = names.find((n) => n.toLowerCase() === w);
  if (exact) return exact;
  const pre = names.filter((n) => n.toLowerCase().startsWith(w));
  if (pre.length === 1 && w.length >= 2) return pre[0]!;
  return null;
}

const SORT_LEADS = new Set(['by', 'most', 'highest', 'biggest', 'largest', 'least', 'lowest', 'fewest']);

/** Does an operator ("> ", "over", "more than", ...) start at word `i`? */
function opAt(words: readonly string[], i: number): boolean {
  if (OPS[words[i] ?? '']) return true;
  return OP_PHRASES.some(([phrase]) => phrase.every((p, k) => words[i + k] === p));
}

/**
 * Is word `i` a field ("cost > 1", "by cost", "most spend") rather than the name of an entity?
 * Words such as cost, spend and usage are both.
 */
function usedAsField(words: readonly string[], i: number): boolean {
  const w = words[i]!;
  if (COMPARE_WORDS[w] && opAt(words, i + 1)) return true;
  // "over 1 cost" / "more than 20 turns": the number and the operator come first.
  if (COMPARE_WORDS[w] && i >= 2 && parseNumber(words[i - 1]!) != null && opAt(words, i - 2)) return true;
  if (COMPARE_WORDS[w] && i >= 3 && parseNumber(words[i - 1]!) != null && opAt(words, i - 3)) return true;
  if (SORT_WORDS[w] && i >= 1 && SORT_LEADS.has(words[i - 1]!)) return true;
  return false;
}

/** Parse a question or a `where=` filter. Never throws; words it cannot place become a text search. */
export function parseAsk(input: string, ctx: AskContext): AskQuery {
  const raw = input.trim();
  const text = raw.replace(/^[?>\s]+/, '').replace(/[?.!]+$/, '').toLowerCase();
  const words = tokenize(text);
  const q: AskQuery = {
    entity: ctx.defaultEntity ?? 'sessions',
    entityNamed: false,
    clauses: [],
    groupBy: null,
    sort: null,
    limit: null,
    count: false,
    raw,
  };
  const textWords: string[] = [];
  let startedField = false;
  let implied: AskEntity | null = null;

  // Pass 1: the entity, so status words resolve against it. A word that is a field here
  // ("cost > 1", "by spend") does not name one, and a `where=` never changes it.
  let entityAt = -1;
  for (let i = 0; i < words.length && !ctx.fixedEntity; i++) {
    const e = ENTITY_WORDS[words[i]!];
    if (e && !usedAsField(words, i)) {
      q.entity = e;
      q.entityNamed = true;
      entityAt = i;
      break;
    }
  }
  if (!q.entityNamed && !ctx.fixedEntity) {
    for (const w of words) {
      const imp = STATUS_IMPLIES[w];
      if (imp && !ctx.defaultEntity) {
        implied = imp;
        break;
      }
    }
    if (implied) q.entity = implied;
  }
  const statusWords = () => STATUS_WORDS[q.entity];

  const pushTime = (window: TimeWindow) => {
    q.clauses = q.clauses.filter((c) => c.kind !== 'time');
    q.clauses.push({ kind: 'time', field: startedField ? 'started' : 'activity', window });
  };

  for (let i = 0; i < words.length; i++) {
    const w = words[i]!;
    const next = words[i + 1];
    const next2 = words[i + 2];

    if (w.startsWith('"') && w.endsWith('"') && w.length > 1) {
      const v = w.slice(1, -1).trim();
      if (v) q.clauses.push({ kind: 'text', value: v });
      continue;
    }
    if (i === entityAt || (ENTITY_WORDS[w] && ENTITY_WORDS[w] === q.entity && !usedAsField(words, i) && !(w === 'action' && next))) continue;

    // key:value, the canonical form for names (project:"my repo", machine:build-01)
    const kv = /^(project|machine|actor):(.+)$/.exec(w);
    if (kv && kv[1] === 'actor') {
      q.clauses.push({ kind: 'actor', value: kv[2]!, exact: true });
      continue;
    }
    if (kv) {
      const names = kv[1] === 'project' ? ctx.projects : ctx.machines;
      const name = resolveName(kv[2]!, names) ?? kv[2]!;
      q.clauses.push(kv[1] === 'project' ? { kind: 'project', name } : { kind: 'machine', name });
      continue;
    }

    // count / how many / number of
    if (w === 'count' || w === 'total') {
      q.count = true;
      continue;
    }
    if (w === 'how' && next === 'many') {
      q.count = true;
      i++;
      continue;
    }
    if (w === 'number' && next === 'of') {
      q.count = true;
      i++;
      continue;
    }

    // Time
    if (w === 'today') {
      pushTime({ kind: 'today' });
      continue;
    }
    if (w === 'yesterday') {
      pushTime({ kind: 'yesterday' });
      continue;
    }
    if ((w === 'this' || w === 'past' || w === 'last') && (next === 'week' || next === 'month')) {
      pushTime(next === 'month' ? { kind: 'month' } : w === 'last' ? { kind: 'lastweek' } : { kind: 'week' });
      i++;
      continue;
    }
    if ((w === 'last' || w === 'past') && next && (next === 'hour' || next === 'day' || next === 'minute')) {
      pushTime({ kind: 'last', n: 1, unit: UNITS[next]! });
      i++;
      continue;
    }
    if ((w === 'last' || w === 'past' || w === 'within') && next) {
      const rel = /^(\d+)([a-z]+)?$/.exec(next);
      if (rel) {
        const unit = rel[2] ? UNITS[rel[2]] : next2 ? UNITS[next2] : undefined;
        if (unit) {
          pushTime({ kind: 'last', n: Number(rel[1]), unit });
          i += rel[2] ? 1 : 2;
          continue;
        }
      }
    }
    {
      const rel = /^(\d+)(m|h|d|w)$/.exec(w);
      if (rel && !next?.match(/^(tokens?|turns?)$/)) {
        pushTime({ kind: 'last', n: Number(rel[1]), unit: UNITS[rel[2]!]! });
        continue;
      }
    }
    if (w === 'started' || w === 'launched' || w === 'created' || w === 'new') {
      if (next === 'here' || next === 'from' && next2 === 'orgus') {
        q.clauses.push({ kind: 'origin', value: 'orgus' });
        i += next === 'here' ? 1 : 2;
        continue;
      }
      startedField = true;
      const t = q.clauses.find((c) => c.kind === 'time');
      if (t && t.kind === 'time') t.field = 'started';
      continue;
    }
    if (w === 'since' && next) {
      if (next === 'yesterday') pushTime({ kind: 'yesterday' });
      else if (next === 'monday' || next === 'week') pushTime({ kind: 'week' });
      else if (next === 'today' || next === 'midnight' || next === 'morning') pushTime({ kind: 'today' });
      else continue;
      i++;
      continue;
    }

    // Grouping
    if ((w === 'by' || w === 'per' || w === 'grouped' || w === 'group') && next) {
      let j = i + 1;
      if ((w === 'grouped' || w === 'group') && words[j] === 'by') j++;
      const g = GROUP_WORDS[words[j] ?? ''];
      if (g) {
        q.groupBy = g;
        i = j;
        continue;
      }
      if (w === 'by' && q.entity === 'audit' && words[j]) {
        q.clauses.push({ kind: 'actor', value: words[j]! });
        i = j;
        continue;
      }
      if (w === 'by') {
        const s = SORT_WORDS[words[j] ?? ''];
        if (s) {
          q.sort = { field: s, dir: s === 'name' ? 'asc' : 'desc' };
          i = j;
          continue;
        }
      }
    }

    // Sorting
    if ((w === 'sort' || w === 'sorted' || w === 'order' || w === 'ordered') && next) {
      let j = i + 1;
      if (words[j] === 'by') j++;
      const s = SORT_WORDS[words[j] ?? ''];
      if (s) {
        let dir: 'asc' | 'desc' = s === 'name' ? 'asc' : 'desc';
        if (words[j + 1] === 'asc' || words[j + 1] === 'ascending') {
          dir = 'asc';
          j++;
        } else if (words[j + 1] === 'desc' || words[j + 1] === 'descending') {
          dir = 'desc';
          j++;
        }
        q.sort = { field: s, dir };
        i = j;
        continue;
      }
    }
    if ((w === 'most' || w === 'highest' || w === 'biggest' || w === 'largest') && next && SORT_WORDS[next]) {
      q.sort = { field: SORT_WORDS[next]!, dir: 'desc' };
      i++;
      continue;
    }
    if ((w === 'least' || w === 'lowest' || w === 'fewest') && next && SORT_WORDS[next]) {
      q.sort = { field: SORT_WORDS[next]!, dir: 'asc' };
      i++;
      continue;
    }
    if (w === 'newest' || w === 'latest' || w === 'recent') {
      q.sort = { field: q.entity === 'audit' ? 'time' : 'activity', dir: 'desc' };
      continue;
    }
    if (w === 'oldest') {
      q.sort = { field: q.entity === 'audit' ? 'time' : 'started', dir: 'asc' };
      continue;
    }
    if ((w === 'top' || w === 'first' || w === 'limit') && next && /^\d+$/.test(next)) {
      q.limit = Math.min(1000, Number(next));
      i++;
      continue;
    }

    // Comparisons: "tokens > 1m", "load over 80%", "more than 20 turns".
    const cf = COMPARE_WORDS[w];
    if (cf && next) {
      let j = i + 1;
      let op = OPS[words[j]!];
      if (!op) {
        for (const [phrase, o] of OP_PHRASES) {
          if (phrase.every((p, k) => words[j + k] === p)) {
            op = o;
            j += phrase.length - 1;
            break;
          }
        }
      }
      if (op) {
        const n = parseNumber(words[j + 1] ?? '');
        if (n != null) {
          q.clauses.push({ kind: 'compare', field: cf, op, value: n });
          i = j + 1;
          continue;
        }
      }
    }
    {
      // "over 20 turns", "more than 1m tokens", "> 5 subagents"
      let op: CompareOp | undefined = OPS[w];
      let j = i;
      if (!op) {
        for (const [phrase, o] of OP_PHRASES) {
          if (phrase.every((p, k) => words[i + k] === p)) {
            op = o;
            j = i + phrase.length - 1;
            break;
          }
        }
      }
      if (op) {
        const n = parseNumber(words[j + 1] ?? '');
        const f = COMPARE_WORDS[words[j + 2] ?? ''];
        if (n != null && f) {
          q.clauses.push({ kind: 'compare', field: f, op, value: n });
          i = j + 2;
          continue;
        }
      }
    }

    // With subagents / workflows / changes
    if ((w === 'with' || w === 'having' || w === 'has') && next) {
      if (next === 'subagents' || next === 'subagent') {
        q.clauses.push({ kind: 'has', value: 'subagents' });
        i++;
        continue;
      }
      if (next === 'workflows' || next === 'workflow') {
        q.clauses.push({ kind: 'has', value: 'workflows' });
        i++;
        continue;
      }
      if (next === 'changes' || next === 'uncommitted') {
        q.clauses.push({ kind: 'has', value: 'changes' });
        i++;
        continue;
      }
    }

    // Machine and project, by preposition or bare name.
    if ((w === 'on' || w === 'at') && next) {
      const name = resolveName(next, ctx.machines);
      q.clauses.push({ kind: 'machine', name: name ?? unquote(next) });
      i++;
      continue;
    }
    if ((w === 'in' || w === 'for' || w === 'project') && next && !(w === 'in' && (next === 'the' || next === 'last' || next === 'past'))) {
      const name = resolveName(next, ctx.projects);
      if (name || w === 'project') {
        q.clauses.push({ kind: 'project', name: name ?? unquote(next) });
        i++;
        continue;
      }
    }
    if (w === 'machine' && next && q.entity !== 'machines') {
      q.clauses.push({ kind: 'machine', name: resolveName(next, ctx.machines) ?? unquote(next) });
      i++;
      continue;
    }
    if (w === 'model' && next) {
      q.clauses.push({ kind: 'model', value: next });
      i++;
      continue;
    }
    if (MODELS.includes(w)) {
      q.clauses.push({ kind: 'model', value: w });
      continue;
    }
    if (w === 'actor' && next) {
      q.clauses.push({ kind: 'actor', value: next });
      i++;
      continue;
    }
    if (w === 'action' && next) {
      const k = (ACTION_KINDS as readonly string[]).includes(next) ? (next as ActionKind) : ACTION_WORDS[next];
      if (k) {
        q.clauses.push({ kind: 'action', value: k });
        i++;
        continue;
      }
    }
    if (q.entity === 'audit') {
      const k = (ACTION_KINDS as readonly string[]).includes(w) ? (w as ActionKind) : ACTION_WORDS[w];
      if (k) {
        q.clauses.push({ kind: 'action', value: k });
        continue;
      }
    }

    // "needs you" / "needing me" / "not reporting"
    if ((w === 'needs' || w === 'needing') && (next === 'you' || next === 'me' || next === 'attention')) {
      if (q.entity === 'sessions') q.clauses.push({ kind: 'status', value: 'waiting' });
      i++;
      continue;
    }
    if (w === 'not' && next === 'reporting') {
      if (q.entity === 'machines') q.clauses.push({ kind: 'status', value: 'stale' });
      i++;
      continue;
    }

    // Status words for this entity.
    const st = statusWords()[w];
    if (st) {
      if (!q.clauses.some((c) => c.kind === 'status' && c.value === st)) q.clauses.push({ kind: 'status', value: st });
      continue;
    }

    // Bare machine or project names.
    const m = ctx.machines.find((n) => n.toLowerCase() === w);
    if (m && q.entity !== 'machines') {
      q.clauses.push({ kind: 'machine', name: m });
      continue;
    }
    const p = ctx.projects.find((n) => n.toLowerCase() === w);
    if (p && q.entity !== 'projects') {
      q.clauses.push({ kind: 'project', name: p });
      continue;
    }

    if (FILLER.has(w) || ENTITY_WORDS[w] || w === 'by' || w === 'with') continue;
    if (w.length >= 2) textWords.push(w);
  }
  if (textWords.length) q.clauses.push({ kind: 'text', value: textWords.join(' ') });
  if (q.entity === 'spend' && !q.groupBy) q.groupBy = 'project';
  return q;
}

// ---------------------------------------------------------------------------
// Back to text: the canonical clause words, for `where=` and for display.
// ---------------------------------------------------------------------------

const UNIT_SHORT: Record<string, string> = { minute: 'minutes', hour: 'hours', day: 'days', week: 'weeks' };

export function windowWords(w: TimeWindow): string {
  switch (w.kind) {
    case 'today':
      return 'today';
    case 'yesterday':
      return 'yesterday';
    case 'week':
      return 'this week';
    case 'lastweek':
      return 'last week';
    case 'month':
      return 'this month';
    case 'last':
      return w.n === 1 ? `last ${w.unit}` : `last ${w.n} ${UNIT_SHORT[w.unit]}`;
  }
}

function quoteName(n: string): string {
  return /\s/.test(n) ? `"${n}"` : n;
}

function numWords(n: number, field: CompareField): string {
  if (field === 'load' || field === 'memory') return `${Math.round(n * 100)}%`;
  if (n >= 1e6 && n % 1e5 === 0) return `${n / 1e6}m`;
  if (n >= 1e3 && n % 100 === 0) return `${n / 1e3}k`;
  return String(n);
}

/** One clause in the words the parser reads back to the same clause. */
export function clauseWords(c: Clause): string {
  switch (c.kind) {
    case 'status':
      return c.value;
    case 'time':
      return c.field === 'started' ? `started ${windowWords(c.window)}` : windowWords(c.window);
    case 'machine':
      return `on ${quoteName(c.name)}`;
    case 'project':
      return `project:${quoteName(c.name)}`;
    case 'model':
      return `model ${c.value}`;
    case 'text':
      return `"${c.value}"`;
    case 'compare':
      return `${c.field} ${c.op} ${numWords(c.value, c.field)}`;
    case 'actor':
      return c.exact ? `actor:${quoteName(c.value)}` : `actor ${c.value}`;
    case 'action':
      return `action ${c.value}`;
    case 'has':
      return `with ${c.value}`;
    case 'origin':
      return 'started here';
  }
}

/** The clauses as `where=` text (no entity, grouping or sort: those have their own parameters). */
export function whereText(clauses: readonly Clause[]): string {
  return clauses.map(clauseWords).join(' ');
}
